import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Moon, Car, ArrowRight, CheckCircle2, Trophy, BookOpen } from 'lucide-react';
import { Button } from './ui/button';

const DebriefCard = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);

  useEffect(() => { fetchStatus(); }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/api/debrief/current');
      const { debrief, is_available } = res.data;
      // Show CTA on Fri/Sat/Sun if not completed/skipped, show summary if completed on Fri/Sat/Sun
      const shouldShow = (is_available && (!debrief || debrief.statut === 'en_cours')) ||
        (debrief && debrief.statut === 'completee' && is_available);
      setVisible(shouldShow);
      setData(res.data);
    } catch (err) { setVisible(false); }
    finally { setLoading(false); }
  };

  if (loading || !visible) return null;

  const debrief = data?.debrief;
  const isCompleted = debrief?.statut === 'completee';
  const isSkipped = debrief?.statut === 'skippee';
  const isInProgress = debrief?.statut === 'en_cours';

  if (isSkipped) return null;

  // Completed summary
  if (isCompleted) {
    return (
      <div data-testid="debrief-card-summary" className="glass-card p-6 border-atlasPurple/20" style={{ boxShadow: '0 0 20px rgba(139, 92, 246, 0.08)' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-atlasPurple/10 rounded-lg">
            <Moon className="w-5 h-5 text-atlasPurple" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-semibold">{t('debrief.cardSummaryTitle')}</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-atlasPurple" />
              <span className="text-xs text-atlasPurple">{t('debrief.completed')}</span>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          {debrief.victoire && (
            <div>
              <p className="text-xs text-atlasTextSecondary mb-1 flex items-center gap-1.5">
                <Trophy className="w-3 h-3" /> {t('debrief.yourVictory')}
              </p>
              <p className="text-sm text-white">{debrief.victoire}</p>
            </div>
          )}
          {debrief.apprentissage && (
            <div>
              <p className="text-xs text-atlasTextSecondary mb-1 flex items-center gap-1.5">
                <BookOpen className="w-3 h-3" /> {t('debrief.yourLearning')}
              </p>
              <p className="text-sm text-white">{debrief.apprentissage}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // CTA
  return (
    <div data-testid="debrief-card-cta" className="glass-card p-6 border-atlasPurple/20" style={{ boxShadow: '0 0 20px rgba(139, 92, 246, 0.08)' }}>
      <div className="flex items-start gap-4">
        <div className="p-3 bg-atlasPurple/10 rounded-xl flex-shrink-0">
          <Moon className="w-7 h-7 text-atlasPurple" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white">{t('debrief.title')}</h3>
          <p className="text-sm text-atlasTextSecondary mt-1">
            {isInProgress ? t('debrief.resumeSubtitle') : t('debrief.subtitle')}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <Button data-testid="debrief-start-btn" onClick={() => navigate('/debrief')} className="bg-atlasPurple hover:bg-atlasPurple/80 text-white flex items-center gap-2">
              {isInProgress ? t('debrief.resume') : t('debrief.start')}
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button data-testid="debrief-driving-btn" onClick={() => navigate('/driving')} variant="outline" className="border-atlasTextSecondary text-atlasTextSecondary hover:text-white flex items-center gap-2">
              <Car className="w-4 h-4" />
              {t('debrief.drivingMode')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebriefCard;
